import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  AkaAdviceCheckboxes,
  AkaAdvicePriceFormValues,
} from "../../interfaces";

import CollapsibleContainer from "Components/CollapsibleContainer";
import { DetailsCardWrapper } from "Components/InsuranceDetailsCard/styled";
import { VALUE_TYPE } from "Components/NumberValue";
import { useAdviceDetailsFormValues } from "Features/CheckPrice/Products/Advice/ProductPrice/hooks/stateSelectors";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { useAkaAdviceBaseAmountSelector } from "Store/akaAdvice/akaAdviceSelectors";
import { getPaymentRestriction } from "Store/akaAdvice/interfaces/getters";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { akaAdvicePriceCalculationRequestStart } from "Store/akaAdvice/slice";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useIsAkademikerneBranding } from "Store/partners/partnerHooks";
import { TextLanguage } from "Store/texts/interfaces";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { AdviceCardComment } from "../../AdviceCardComment";
import { AdviceCardHeader } from "../../AdviceCardHeader";
import { CardTotalPrice } from "../../AdvicePrice/CardTotalPrice";
import { AdvisorUwInfo } from "../../AdvisorUwInfo";
import { AkaAdviceChartCriticalIllness } from "../../AkaAdviceCharts/AkaAdviceChartCriticallIllness";
import { Summary } from "../../Summary/Summary";
import { usePriceStatus } from "../../hooks/usePriceStatus";
import { ContentWrapper } from "../../styled";
import { AkaCriticalIllnessCoInsuredForm } from "../Forms/AkaCriticalIllnessCoInsuredForm";
import { AkaCriticalIllnessForm } from "../Forms/AkaCriticalIllnessForm";
import { CriticalIllnessForm } from "../Forms/CriticalIllnessForm";
import { AkaCriticalIllnessAdviceText } from "../Texts/AkaCriticalIllnessAdviceText";

const NEED_NAME = NeedName.CRITICAL_ILLNESS;

export interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  adviceCard: any;
  lang: TextLanguage;
}

export const AkaCriticalIllnessAdviceCard = ({
  formProps,
  need,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const isAkaBranding = useIsAkademikerneBranding();
  const dispatch = useDispatch();
  const adviceDetails = useAdviceDetailsFormValues();
  const isAdvisor = useIsAdvisorSelector();
  const baseAmount = useAkaAdviceBaseAmountSelector();
  const selected = formProps.values.criticalIllnessCheckbox;
  const currentSliderValue = formProps.values.criticalIllnessInsuredSumSlider;
  const {
    priceIsChanging,
    setPriceIsChanging,
    priceIsLoading,
    setPriceIsLoading,
  } = usePriceStatus();
  const insuranceType: string = adviceCard.insuranceType[lang];

  const recalculatePrice = (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ): void => {
    setPriceIsChanging(false);
    setPriceIsLoading(true);
    dispatch(
      akaAdvicePriceCalculationRequestStart({
        isCollectiveAdvice: isAkaBranding,
        priceFormValues: formValues,
        specificProduct,
      }),
    );
  };

  if (!adviceDetails) {
    return null;
  }

  const adviceValueAkaCriticalIllness = getCorrectAkaAdviceValues(
    need,
    ProductCoverageName.AKA_CRITICAL_ILLNESS,
    isAdvisor,
  ).adviceValue;
  const adviceValueCriticalIllness = getCorrectAkaAdviceValues(
    need,
    ProductCoverageName.CRITICAL_ILLNESS,
    isAdvisor,
  ).adviceValue;

  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.CRITICAL_ILLNESS,
  );

  const recommendedInsuranceSum =
    adviceValueAkaCriticalIllness * baseAmount + adviceValueCriticalIllness;

  return (
    <div className="AdviceCard_CRITICAL_ILLNESS">
      <DetailsCardWrapper
        key={insuranceType}
        adviceDetailsCard
        selected={selected}
      >
        <AdviceCardHeader
          insuranceId={AkaAdviceCheckboxes.CRITICAL_ILLNESS_CHECKBOX}
          insuranceType={insuranceType}
        />
        <ContentWrapper className="AdviceCardContent">
          <Summary
            text="Vi anbefaler en engangsutbetaling på"
            sum={recommendedInsuranceSum}
            valueType={VALUE_TYPE.NOK}
          />
          <AkaCriticalIllnessAdviceText
            need={need}
            adviceTexts={{
              ...adviceCard.adviceTexts,
              adviceUnavailableMessages: adviceCard.adviceUnavailableMessages,
            }}
            lang={lang}
          />
          <CollapsibleContainer isOpen={selected}>
            <AkaAdviceChartCriticalIllness
              formProps={formProps}
              income={adviceDetails.income}
            />

            <AkaCriticalIllnessForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              priceIsChanging={priceIsChanging}
              priceIsLoading={priceIsLoading}
              adviceCard={adviceCard}
              lang={lang}
            />

            {formProps.values.akaCriticalIllnessCoverageAmountCheckbox &&
              adviceDetails.addCoInsured === BooleanFormType.TRUE && (
                <AkaCriticalIllnessCoInsuredForm
                  formProps={formProps}
                  recalculatePrice={recalculatePrice}
                  priceIsChanging={priceIsChanging}
                  priceIsLoading={priceIsLoading}
                  coInsuredBirthDate={adviceDetails.coInsuredBirthDate}
                />
              )}

            <CriticalIllnessForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              setPriceIsChanging={setPriceIsChanging}
              priceIsChanging={priceIsChanging}
              priceIsLoading={priceIsLoading}
              adviceCard={adviceCard}
              lang={lang}
            />

            {isAdvisor && (
              <AdvisorUwInfo
                insuranceName={INSURANCE_NAME_VALUE.CRITICAL_ILLNESS}
                sliderValue={currentSliderValue ?? 0}
                paymentRestriction={paymentRestriction}
                uwInfoTexts={adviceCard.advisorTexts.uwInfoTexts}
                lang={lang}
              />
            )}
          </CollapsibleContainer>
          <AdviceCardComment
            commentId="criticalIllnessAdviceComment"
            formProps={formProps}
            label="Notater om anbefaling og kundens valg av kritisk sykdom"
          />
          <CardTotalPrice
            formProps={formProps}
            needName={NEED_NAME}
            priceIsLoading={priceIsLoading}
          />
        </ContentWrapper>
      </DetailsCardWrapper>
    </div>
  );
};
